import { SketchLineObject, SketchArcObject, SketchEllipseObject, SketchSplineObject, SketchPointObject, SketchParabolaObject, SketchCircleObject, SketchStyleSplineObject } from '../../mesh';
import { Vector3, Box3, Vector2, EllipseCurve, LineSegments, BufferGeometry, Float32BufferAttribute, LineBasicMaterial } from 'three';
import { SketchEntityExtension } from './SketchEntityExtension';
import { SketchEntityCut } from './SketchEntityCut';
import { SketchEntityNewControlPoint } from './SketchEntityNewControlPoint';
import { pointInPolygon2 } from '../../sketch-analysis/SketchRegionMath';

import type {
    OffsetCandidate, SketchEntityChain, CutPairResult, IntersectJointContext,
} from './type'

/**
 * 唯一功能: 处理多段直线/曲线/圆弧/椭圆弧之间的断开与相交问题, 得到一个流畅的连续轮廓
 * 简介: 对于由多段直线、曲线、圆弧和椭圆弧组成的组合图形, 比如槽口, 多边形, 任意组合图形等,
 *  执行等距实体时, 首先对每一单独的段进行独立偏移, 然后对于断开的地方, 采取延申相交处理, 对于
 *  相交的地方, 舍弃内部多余部分, 从而得到一个流畅的连续轮廓
 *
 * 本类保留: 统一入口 {@link sketchExtensionAndIntersectionProcessing} 及其配套子方法
 *  （isSupported/getEntityEndpoints/resolveJointEnds/intersectPair/cutPair/processPair）；
 *  延申方法见 {@link SketchEntityExtension}，截取方法见 {@link SketchEntityCut}，
 *  求交方法见 {@link SketchEntityIntersect}。
 */
export class SketchExtensionAndIntersectionProcessing {

    private static tmpDebugLineSet = new Set();

    // 1 判断实体是否支持 延长/求交/截取 处理（圆/样式样条/槽口/集合等不支持）
    private static isSupported(entity: OffsetCandidate): boolean {
        return entity instanceof SketchLineObject
            || entity instanceof SketchArcObject
            || entity instanceof SketchEllipseObject
            || entity instanceof SketchParabolaObject
            || entity instanceof SketchSplineObject;
    }

    // 2 获取实体起止端点世界坐标（用于断开判断；不支持的实体返回 null）
    private static getEntityEndpoints(entity: OffsetCandidate): { start: Vector3; end: Vector3 } | null {
        if (entity instanceof SketchLineObject) {
            return { start: entity.startPoint.position, end: entity.endPoint.position };
        }
        if (entity instanceof SketchArcObject
            || entity instanceof SketchEllipseObject
            || entity instanceof SketchParabolaObject) {
            return { start: entity.getStartPoint().position, end: entity.getEndPoint().position };
        }
        if (entity instanceof SketchSplineObject) {
            const controlPoints = entity.getControlPoints();
            return {
                start: controlPoints[0]!.position,
                end: controlPoints[controlPoints.length - 1]!.position,
            };
        }
        return null;
    }

    // 2.5 获取实体起止端点实例（用于短路分支以 a 侧相连端端点实例作为共享交点，
    //     与 getEntityEndpoints 的"位置版"对应；不支持的实体返回 null）
    private static getEntityEndpointObjects(entity: OffsetCandidate):
        { start: SketchPointObject; end: SketchPointObject } | null {
        if (entity instanceof SketchLineObject) {
            return { start: entity.startPoint, end: entity.endPoint };
        }
        if (entity instanceof SketchArcObject
            || entity instanceof SketchEllipseObject
            || entity instanceof SketchParabolaObject) {
            return { start: entity.getStartPoint(), end: entity.getEndPoint() };
        }
        if (entity instanceof SketchSplineObject) {
            const controlPoints = entity.getControlPoints();
            return {
                start: controlPoints[0]!,
                end: controlPoints[controlPoints.length - 1]!,
            };
        }
        return null;
    }

    // 2.6 构建实体的接缝上下文 —— 从延长前的偏移实体提取相连端/自由端端点及
    //     样条/抛物线的原始参数数据，供 SketchEntityNewControlPoint 选取层消费。
    //
    //     背景：旧实现向 intersect* 选取层传 origin 实体，靠"origin 与延长后实体端点
    //     匹配"反推延伸侧；未延长（extend=false）时 origin 与实体完全一致（两端都
    //     匹配），无法反推出接缝侧，导致两实体闭合链在非相连端交叉时误用另一接缝的
    //     交点。改为直接携带相连端信息后，选取层可显式计算延伸方向、出发点并过滤
    //     位于非相连端一侧的交点。
    static buildIntersectJointContext(
        entity: OffsetCandidate,
        side: 'start' | 'end',
    ): IntersectJointContext {
        const endpoints = SketchExtensionAndIntersectionProcessing.getEntityEndpoints(entity);
        if (!endpoints) {
            throw new Error(`[等距实体] 无法为不支持的实体类型构建接缝上下文: ${entity.constructor.name}`);
        }
        const context: IntersectJointContext = {
            side,
            jointEndWorld: (side === 'start' ? endpoints.start : endpoints.end).clone(),
            freeEndWorld: (side === 'start' ? endpoints.end : endpoints.start).clone(),
        };
        // 样条侧：携带原始曲线采样点（计算原始总弧长与起点侧延伸的弧长定位）
        if (entity instanceof SketchSplineObject) {
            context.originCurvePoints = entity.getCurvePoints().map((point) => point.clone());
        } else if (entity instanceof SketchParabolaObject) {
            // 抛物线侧：携带原始起止参数（延长后定义参数已被扩展，原始值丢失）
            const definition = entity.getParabolaDefinition();
            context.originStartParameter = definition.startParameter;
            context.originEndParameter = definition.endParameter;
        }
        return context;
    }

    // 3 解析相邻两实体的接缝端：取距离最近的一对端点（由几何决定，与链顺序无关）
    //     选择链是排序后的数据，相邻实体实际哪两个端点相连只能通过端点距离判断
    //
    //     可选参数 excludeJoint 的作用：仅仅用于"两个自封闭的草图实体"（两实体闭合链）
    //     求解正确接缝点，不用于其他作用。两实体闭合链中同一对实体共享两个接缝
    //     （四个端点的两个配对），按候选顺序平局解析时主循环与闭合首尾轮可能取到
    //     同一接缝；调用方在闭合轮传入主循环已解析的接缝（必须交换 aSide/bSide
    //     归一化为本次调用的参数顺序），若本次计算结果与传入接缝相同，说明又解析到
    //     同一接缝，取反（aSide/bSide 互换）返回另一个接缝。单接缝实体对
    //     （三实体链、开口链）不得传入。
    private static resolveJointEnds(
        a: OffsetCandidate,
        b: OffsetCandidate,
        excludeJoint?: { aSide: 'start' | 'end'; bSide: 'start' | 'end'; minDistSq: number } | null,
    ): { aSide: 'start' | 'end'; bSide: 'start' | 'end'; minDistSq: number } | null {
        const aEndpoints = SketchExtensionAndIntersectionProcessing.getEntityEndpoints(a);
        const bEndpoints = SketchExtensionAndIntersectionProcessing.getEntityEndpoints(b);
        if (!aEndpoints || !bEndpoints) {
            return null;
        }
        // 四组端点组合的距离平方，取最小者作为接缝端
        const candidates: Array<{ aSide: 'start' | 'end'; bSide: 'start' | 'end'; distSq: number }> = [
            {
                aSide: 'start',
                bSide: 'start',
                distSq: aEndpoints.start.distanceToSquared(bEndpoints.start),
            },
            {
                aSide: 'start',
                bSide: 'end',
                distSq: aEndpoints.start.distanceToSquared(bEndpoints.end),
            },
            {
                aSide: 'end',
                bSide: 'start',
                distSq: aEndpoints.end.distanceToSquared(bEndpoints.start),
            },
            {
                aSide: 'end',
                bSide: 'end',
                distSq: aEndpoints.end.distanceToSquared(bEndpoints.end),
            },
        ];
        let best = candidates[0]!;
        let bestIndex = 0;
        for (let index = 0; index < candidates.length; index += 1) {
            const candidate = candidates[index]!;
            if (candidate.distSq < best.distSq) {
                best = candidate;
                bestIndex = index;
            }
        }
        // 两实体闭合链排除接缝：本次解析结果与传入接缝相同（同一接缝）时，取反返回
        // 另一个接缝（对角候选映射 0↔3、1↔2）；minDistSq 必须重算为取反侧端点对的
        // 真实距离——偏移后数据上两个接缝距离不同，复用 best.distSq 会产生错误值
        if (excludeJoint
            && best.aSide === excludeJoint.aSide
            && best.bSide === excludeJoint.bSide) {
            const opposite = candidates[3 - bestIndex]!;
            return {
                aSide: opposite.aSide,
                bSide: opposite.bSide,
                minDistSq: opposite.distSq,
            };
        }
        return { aSide: best.aSide, bSide: best.bSide, minDistSq: best.distSq };
    }

    // 3 求交分发：按 a/b 类型组合调用对应 intersect 函数；
    //     extend=false 时直接用原实体求交（用于"是否相交"判断）；
    //     extend=true 时按接缝端（joint，由端点距离判定，与链顺序无关）延长后求交：
    //     接缝在 a 终点则延长 a 终点侧，接缝在 a 起点则延长 a 起点侧（b 同理），
    //     弧/椭圆补全同样按接缝侧定向延伸（固定端角度不变、延伸端绕满 2π）；
    //     接缝上下文（IntersectJointContext）从延长前实体构建并透传给选取层，
    //     替代旧实现传 origin 实体反推延伸侧的间接做法（未延长时反推不出接缝侧）；
    //     isTwoEntityClosedLoop 透传给选取层的接缝侧过滤：单候选交点仅在两实体
    //     自封闭图形场景存在"另一接缝交叉点"伪交点风险，需要过滤，其余场景不筛
    private static intersectPair(
        a: OffsetCandidate,
        b: OffsetCandidate,
        extend: boolean,
        joint: { aSide: 'start' | 'end'; bSide: 'start' | 'end'; minDistSq: number },
        isTwoEntityClosedLoop: boolean,
    ): SketchPointObject[] {
        // 按接缝端动态推导延长方向：接缝在终点侧则延长终点侧，接缝在起点侧则延长起点侧
        const aDir = joint.aSide === 'end' ? { header: false, end: true } : { header: true, end: false };
        const bDir = joint.bSide === 'end' ? { header: false, end: true } : { header: true, end: false };
        // 构建 a/b 的接缝上下文（延长前实体 + 相连端），供选取层计算延伸方向/出发点
        // 并过滤非相连侧交点；processPair 的 isSupported 门保证实体类型受支持，上下文必可构建
        const aJointContext = SketchExtensionAndIntersectionProcessing.buildIntersectJointContext(a, joint.aSide);
        const bJointContext = SketchExtensionAndIntersectionProcessing.buildIntersectJointContext(b, joint.bSide);

        // 线-线：与其它组合一致，extend=true 时先延长再求交；
        // 求交模块只接受线段本身（交点必须落在线段可见区间内），延长语义由 SketchEntityExtension 承担。
        // 线-线求交语义等价于"两条无限直线求交"：交点可能落在接缝侧或非接缝侧的任意延长线上，
        // 故必须双侧延长（不能只延长接缝侧），否则非接缝侧延长线上的交点会被可见区间过滤丢弃
        if (a instanceof SketchLineObject && b instanceof SketchLineObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionLine(a, {header: true, end: true}) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionLine(b, {header: true, end: true}) : b;
            return SketchEntityNewControlPoint.intersectLineLine(aExt, bExt);
        }
        // 线-弧
        if (a instanceof SketchLineObject && b instanceof SketchArcObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionLine(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionArc(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectLineArc(aExt, bExt, aJointContext, isTwoEntityClosedLoop);
        }
        // 线-椭圆
        if (a instanceof SketchLineObject && b instanceof SketchEllipseObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionLine(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionEllipse(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectLineEllipse(aExt, bExt, aJointContext, isTwoEntityClosedLoop);
        }
        // 线-样条
        if (a instanceof SketchLineObject && b instanceof SketchSplineObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionLine(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionSpline(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectLineSpline(aExt, bExt, bJointContext, isTwoEntityClosedLoop);
        }
        // 线-抛物线
        if (a instanceof SketchLineObject && b instanceof SketchParabolaObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionLine(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionParabola(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectLineParabola(aExt, bExt, aJointContext, isTwoEntityClosedLoop);
        }
        // 弧-弧
        if (a instanceof SketchArcObject && b instanceof SketchArcObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionArc(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionArc(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectArcArc(aExt, bExt, aJointContext, isTwoEntityClosedLoop);
        }
        // 弧-椭圆
        if (a instanceof SketchArcObject && b instanceof SketchEllipseObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionArc(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionEllipse(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectArcEllipse(aExt, bExt, aJointContext, isTwoEntityClosedLoop);
        }
        // 弧-抛物线
        if (a instanceof SketchArcObject && b instanceof SketchParabolaObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionArc(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionParabola(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectArcParabola(aExt, bExt, bJointContext, isTwoEntityClosedLoop);
        }
        // 弧-样条
        if (a instanceof SketchArcObject && b instanceof SketchSplineObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionArc(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionSpline(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectArcSpline(aExt, bExt, bJointContext, isTwoEntityClosedLoop);
        }
        // 椭圆-椭圆
        if (a instanceof SketchEllipseObject && b instanceof SketchEllipseObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionEllipse(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionEllipse(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectEllipseEllipse(aExt, bExt, aJointContext, isTwoEntityClosedLoop);
        }
        // 椭圆-样条
        if (a instanceof SketchEllipseObject && b instanceof SketchSplineObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionEllipse(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionSpline(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectEllipseSpline(aExt, bExt, bJointContext, isTwoEntityClosedLoop);
        }
        // 椭圆-抛物线
        if (a instanceof SketchEllipseObject && b instanceof SketchParabolaObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionEllipse(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionParabola(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectEllipseParabola(aExt, bExt, bJointContext, isTwoEntityClosedLoop);
        }
        // 样条-样条
        if (a instanceof SketchSplineObject && b instanceof SketchSplineObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionSpline(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionSpline(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectSplineSpline(aExt, bExt, aJointContext, isTwoEntityClosedLoop);
        }
        // 样条-抛物线
        if (a instanceof SketchSplineObject && b instanceof SketchParabolaObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionSpline(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionParabola(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectSplineParabola(aExt, bExt, bJointContext, isTwoEntityClosedLoop);
        }
        // 抛物线-抛物线
        if (a instanceof SketchParabolaObject && b instanceof SketchParabolaObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionParabola(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionParabola(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectParabolaParabola(aExt, bExt, aJointContext, isTwoEntityClosedLoop);
        }
        // —— 逆序组合（a/b 类型次序与正序函数不一致）：调用正序函数并交换参数 ——
        // 弧-线
        if (a instanceof SketchArcObject && b instanceof SketchLineObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionArc(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionLine(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectLineArc(bExt, aExt, bJointContext, isTwoEntityClosedLoop);
        }
        // 椭圆-线
        if (a instanceof SketchEllipseObject && b instanceof SketchLineObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionEllipse(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionLine(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectLineEllipse(bExt, aExt, bJointContext, isTwoEntityClosedLoop);
        }
        // 样条-线
        if (a instanceof SketchSplineObject && b instanceof SketchLineObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionSpline(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionLine(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectLineSpline(bExt, aExt, aJointContext, isTwoEntityClosedLoop);
        }
        // 抛物线-线
        if (a instanceof SketchParabolaObject && b instanceof SketchLineObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionParabola(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionLine(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectLineParabola(bExt, aExt, bJointContext, isTwoEntityClosedLoop);
        }
        // 椭圆-弧
        if (a instanceof SketchEllipseObject && b instanceof SketchArcObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionEllipse(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionArc(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectArcEllipse(bExt, aExt, bJointContext, isTwoEntityClosedLoop);
        }
        // 抛物线-弧
        if (a instanceof SketchParabolaObject && b instanceof SketchArcObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionParabola(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionArc(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectArcParabola(bExt, aExt, aJointContext, isTwoEntityClosedLoop);
        }
        // 样条-弧
        if (a instanceof SketchSplineObject && b instanceof SketchArcObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionSpline(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionArc(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectArcSpline(bExt, aExt, aJointContext, isTwoEntityClosedLoop);
        }
        // 样条-椭圆
        if (a instanceof SketchSplineObject && b instanceof SketchEllipseObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionSpline(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionEllipse(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectEllipseSpline(bExt, aExt, aJointContext, isTwoEntityClosedLoop);
        }
        // 抛物线-椭圆
        if (a instanceof SketchParabolaObject && b instanceof SketchEllipseObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionParabola(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionEllipse(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectEllipseParabola(bExt, aExt, aJointContext, isTwoEntityClosedLoop);
        }
        // 抛物线-样条
        if (a instanceof SketchParabolaObject && b instanceof SketchSplineObject) {
            const aExt = extend ? SketchEntityExtension.sketchExtensionParabola(a, aDir) : a;
            const bExt = extend ? SketchEntityExtension.sketchExtensionSpline(b, bDir) : b;
            return SketchEntityNewControlPoint.intersectSplineParabola(bExt, aExt, aJointContext, isTwoEntityClosedLoop);
        }
        return [];
    }

    // 4 截取分发：按 a/b 类型组合调用对应 cut 函数（用原实体 + 交点截取）
    //     返回值约定：
    //     - 边界线段被完全吃掉（交点越过非相连端）时为 null，调用方跳过该实体（不渲染）；
    //     - 弧/椭圆弧交点"绕回"非法（沿延长方向超过 180°）时该侧截取结果为 null，
    //       本函数将其升级为返回空数组 []，由调用方终止本链处理并整链丢弃（不渲染）；
    //     入参 aIsBoundary/bIsBoundary：a/b 是否为边界线段（开放链首/尾，由调用方按原始链位置判定）
    private static cutPair(
        a: OffsetCandidate,
        b: OffsetCandidate,
        point: SketchPointObject,
        joint: { aSide: 'start' | 'end'; bSide: 'start' | 'end'; minDistSq: number },
        aIsBoundary: boolean,
        bIsBoundary: boolean,
    ): CutPairResult<OffsetCandidate, OffsetCandidate> | [] {
        if (a instanceof SketchLineObject && b instanceof SketchLineObject) {
            return SketchEntityCut.cutLineLine(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
        }
        if (a instanceof SketchLineObject && b instanceof SketchArcObject) {
            const [line2, arc2] = SketchEntityCut.cutLineArc(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
            // 弧交点"绕回"非法（null）：升级为整链失效（返回 []，调用方终止本链并整链丢弃）
            return arc2 === null ? [] : [line2, arc2];
        }
        if (a instanceof SketchLineObject && b instanceof SketchEllipseObject) {
            const [line2, ellipse2] = SketchEntityCut.cutLineEllipse(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
            // 椭圆弧交点"绕回"非法（null）：升级为整链失效
            return ellipse2 === null ? [] : [line2, ellipse2];
        }
        if (a instanceof SketchLineObject && b instanceof SketchSplineObject) {
            return SketchEntityCut.cutLineSpline(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
        }
        if (a instanceof SketchLineObject && b instanceof SketchParabolaObject) {
            return SketchEntityCut.cutLineParabola(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
        }
        if (a instanceof SketchArcObject && b instanceof SketchArcObject) {
            const [arc1New, arc2New] = SketchEntityCut.cutArcArc(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
            // 任一侧弧交点"绕回"非法（null）：升级为整链失效
            return (arc1New === null || arc2New === null) ? [] : [arc1New, arc2New];
        }
        if (a instanceof SketchArcObject && b instanceof SketchEllipseObject) {
            const [arc2, ellipse2] = SketchEntityCut.cutArcEllipse(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
            // 弧或椭圆弧交点"绕回"非法（null）：升级为整链失效
            return (arc2 === null || ellipse2 === null) ? [] : [arc2, ellipse2];
        }
        if (a instanceof SketchArcObject && b instanceof SketchParabolaObject) {
            const [arc2, parabola2] = SketchEntityCut.cutArcParabola(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
            // 弧交点"绕回"非法（null）：升级为整链失效
            return arc2 === null ? [] : [arc2, parabola2];
        }
        if (a instanceof SketchArcObject && b instanceof SketchSplineObject) {
            const [arc2, spline2] = SketchEntityCut.cutArcSpline(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
            // 弧交点"绕回"非法（null）：升级为整链失效
            return arc2 === null ? [] : [arc2, spline2];
        }
        if (a instanceof SketchEllipseObject && b instanceof SketchEllipseObject) {
            const [ellipse1New, ellipse2New] = SketchEntityCut.cutEllipseEllipse(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
            // 任一侧椭圆弧交点"绕回"非法（null）：升级为整链失效
            return (ellipse1New === null || ellipse2New === null) ? [] : [ellipse1New, ellipse2New];
        }
        if (a instanceof SketchEllipseObject && b instanceof SketchSplineObject) {
            const [ellipse2, spline2] = SketchEntityCut.cutEllipseSpline(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
            // 椭圆弧交点"绕回"非法（null）：升级为整链失效
            return ellipse2 === null ? [] : [ellipse2, spline2];
        }
        if (a instanceof SketchEllipseObject && b instanceof SketchParabolaObject) {
            const [ellipse2, parabola2] = SketchEntityCut.cutEllipseParabola(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
            // 椭圆弧交点"绕回"非法（null）：升级为整链失效
            return ellipse2 === null ? [] : [ellipse2, parabola2];
        }
        if (a instanceof SketchSplineObject && b instanceof SketchSplineObject) {
            return SketchEntityCut.cutSplineSpline(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
        }
        if (a instanceof SketchSplineObject && b instanceof SketchParabolaObject) {
            return SketchEntityCut.cutSplineParabola(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
        }
        if (a instanceof SketchParabolaObject && b instanceof SketchParabolaObject) {
            return SketchEntityCut.cutParabolaParabola(a, b, point, joint.aSide, joint.bSide, aIsBoundary, bIsBoundary);
        }
        // —— 逆序组合（a/b 类型次序与正序函数不一致）：调用正序 cut 并交换结果元组 ——
        // 弧-线
        if (a instanceof SketchArcObject && b instanceof SketchLineObject) {
            const [line2, arc2] = SketchEntityCut.cutLineArc(b, a, point, joint.bSide, joint.aSide, bIsBoundary, aIsBoundary);
            // 弧交点"绕回"非法（null）：升级为整链失效
            return arc2 === null ? [] : [arc2, line2];
        }
        // 椭圆-线
        if (a instanceof SketchEllipseObject && b instanceof SketchLineObject) {
            const [line2, ellipse2] = SketchEntityCut.cutLineEllipse(b, a, point, joint.bSide, joint.aSide, bIsBoundary, aIsBoundary);
            // 椭圆弧交点"绕回"非法（null）：升级为整链失效
            return ellipse2 === null ? [] : [ellipse2, line2];
        }
        // 样条-线
        if (a instanceof SketchSplineObject && b instanceof SketchLineObject) {
            const [line2, spline2] = SketchEntityCut.cutLineSpline(b, a, point, joint.bSide, joint.aSide, bIsBoundary, aIsBoundary);
            return [spline2, line2];
        }
        // 抛物线-线
        if (a instanceof SketchParabolaObject && b instanceof SketchLineObject) {
            const [line2, parabola2] = SketchEntityCut.cutLineParabola(b, a, point, joint.bSide, joint.aSide, bIsBoundary, aIsBoundary);
            return [parabola2, line2];
        }
        // 椭圆-弧
        if (a instanceof SketchEllipseObject && b instanceof SketchArcObject) {
            const [arc2, ellipse2] = SketchEntityCut.cutArcEllipse(b, a, point, joint.bSide, joint.aSide, bIsBoundary, aIsBoundary);
            // 弧或椭圆弧交点"绕回"非法（null）：升级为整链失效
            return (arc2 === null || ellipse2 === null) ? [] : [ellipse2, arc2];
        }
        // 抛物线-弧
        if (a instanceof SketchParabolaObject && b instanceof SketchArcObject) {
            const [arc2, parabola2] = SketchEntityCut.cutArcParabola(b, a, point, joint.bSide, joint.aSide, bIsBoundary, aIsBoundary);
            // 弧交点"绕回"非法（null）：升级为整链失效
            return arc2 === null ? [] : [parabola2, arc2];
        }
        // 样条-弧
        if (a instanceof SketchSplineObject && b instanceof SketchArcObject) {
            const [arc2, spline2] = SketchEntityCut.cutArcSpline(b, a, point, joint.bSide, joint.aSide, bIsBoundary, aIsBoundary);
            // 弧交点"绕回"非法（null）：升级为整链失效
            return arc2 === null ? [] : [spline2, arc2];
        }
        // 样条-椭圆
        if (a instanceof SketchSplineObject && b instanceof SketchEllipseObject) {
            const [ellipse2, spline2] = SketchEntityCut.cutEllipseSpline(b, a, point, joint.bSide, joint.aSide, bIsBoundary, aIsBoundary);
            // 椭圆弧交点"绕回"非法（null）：升级为整链失效
            return ellipse2 === null ? [] : [spline2, ellipse2];
        }
        // 抛物线-椭圆
        if (a instanceof SketchParabolaObject && b instanceof SketchEllipseObject) {
            const [ellipse2, parabola2] = SketchEntityCut.cutEllipseParabola(b, a, point, joint.bSide, joint.aSide, bIsBoundary, aIsBoundary);
            // 椭圆弧交点"绕回"非法（null）：升级为整链失效
            return ellipse2 === null ? [] : [parabola2, ellipse2];
        }
        // 抛物线-样条
        if (a instanceof SketchParabolaObject && b instanceof SketchSplineObject) {
            const [spline2, parabola2] = SketchEntityCut.cutSplineParabola(b, a, point, joint.bSide, joint.aSide, bIsBoundary, aIsBoundary);
            return [parabola2, spline2];
        }
        return [a, b];
    }

    // 5 单对处理：①相交判断 → ②不相交则延长 → ③求交 → ④截取替换（按原始链接缝端截取）
    //     返回值约定：正常返回 [新A, 新B]（边界线段被完全吃掉时为 null，由调用方跳过该实体）；
    //     链无法形成连续轮廓时返回空数组 []，由调用方终止本链处理并整链丢弃（不渲染）——
    //     触发场景：延长后仍无交点、或弧/椭圆弧交点"绕回"非法（沿延长方向超过 180°）
    //     入参 aIsBoundary/bIsBoundary：a/b 是否为边界线段（开放链首/尾，按原始链位置判定）
    //     入参 isTwoEntityClosedLoop：是否为两个草图实体组成的自封闭图形（控制选取层
    //     对单候选交点是否做接缝侧过滤，见 SketchEntityNewControlPoint 过滤方法注释）
    private static processPair(
        a: OffsetCandidate,
        b: OffsetCandidate,
        originalA: OffsetCandidate,
        originalB: OffsetCandidate,
        aIsBoundary: boolean,
        bIsBoundary: boolean,
        isTwoEntityClosedLoop: boolean,
        isAllLine: boolean,
        excludeJoint?: { aSide: 'start' | 'end'; bSide: 'start' | 'end'; minDistSq: number } | null,
    ): CutPairResult<OffsetCandidate, OffsetCandidate> | [] {
        // 不支持的类型（圆/样式样条/槽口/集合）直接保留
        if (!SketchExtensionAndIntersectionProcessing.isSupported(a) || !SketchExtensionAndIntersectionProcessing.isSupported(b)) {
            return [a, b];
        }

        // 解析接缝端：以偏移前原始实体对为准（偏移前相邻实体共享端点/距离≈0，
        // 唯一确定"哪一端与相邻实体相连"）；原始对解析失败（异常拓扑）时回退到
        // 偏移后实体的距离判定，保持旧行为；
        // excludeJoint 仅两实体闭合链闭合轮传入（见 resolveJointEnds 注释），
        // 确保与主循环解析到不同接缝
        const joint = SketchExtensionAndIntersectionProcessing.resolveJointEnds(originalA, originalB, excludeJoint)
            ?? SketchExtensionAndIntersectionProcessing.resolveJointEnds(a, b, excludeJoint);
        if (!joint) {
            return [a, b];
        }

        // 读取偏移后实体的端点位置（joint 侧由偏移前拓扑决定，位置读数必须来自偏移后）
        const aEndpoints = SketchExtensionAndIntersectionProcessing.getEntityEndpoints(a);
        const bEndpoints = SketchExtensionAndIntersectionProcessing.getEntityEndpoints(b);

        // 本接缝（joint 侧）是否已连接：比较 joint 侧端点在偏移后实体上的实际距离。
        // 不能使用"全局最近端点对"（旧 offsetJoint 逻辑）：两实体闭合链中第一轮截取
        // 会把 joint 侧端点顶替到同一交点，全局最近必然命中该伪重合点，导致第二轮
        // （闭合首尾对）被误判"已连接"而短路，第二个交点永远不会被求解
        if (aEndpoints && bEndpoints) {
            const aJointPos = joint.aSide === 'start' ? aEndpoints.start : aEndpoints.end;
            const bJointPos = joint.bSide === 'start' ? bEndpoints.start : bEndpoints.end;
            if (aJointPos.distanceToSquared(bJointPos) < 1e-16) {
                // 本接缝偏移后仍精确相连（如共线相连边平移后端点仍重合）：几何上无需延长求交，
                // 但相连端点必须复用同一实例（组合草图实体相连端共享语义）——
                // 以 a 侧相连端端点实例作为"交点"走统一截取路径，完成端点合并
                const aEndpointObjects = SketchExtensionAndIntersectionProcessing.getEntityEndpointObjects(a);
                if (!aEndpointObjects) {
                    return [a, b];
                }
                const aJointPoint = joint.aSide === 'start' ? aEndpointObjects.start : aEndpointObjects.end;
                return SketchExtensionAndIntersectionProcessing.cutPair(
                    a, b, aJointPoint, joint, aIsBoundary, bIsBoundary,
                );
            }
        }

        let isExtend = false;
        // ① 原实体求交：判断是否已相交（相交则直接用该交点截取）
        let points = SketchExtensionAndIntersectionProcessing.intersectPair(a, b, false, joint, isTwoEntityClosedLoop);

        // ② 伪交点丢弃：两实体闭合链中，上一轮截取会把"非 joint 侧"端点对顶替到
        //    同一交点，使本轮 extend=false 求交返回该已被处理过的交点（而非 joint 侧
        //    接缝交点）；判定特征：非 joint 侧端点对精确重合，且求出的交点就在该重合点处
        if (points.length > 0 && aEndpoints && bEndpoints) {
            const aNonJointPos = joint.aSide === 'start' ? aEndpoints.end : aEndpoints.start;
            const bNonJointPos = joint.bSide === 'start' ? bEndpoints.end : bEndpoints.start;
            if (aNonJointPos.distanceToSquared(bNonJointPos) < 1e-16
                && points[0]!.position.distanceToSquared(aNonJointPos) < 1e-9) {
                points = [];
            }
        }

        // ③ 不相交（断开）→ 接缝侧延长后再求交
        if (points.length === 0) {
            points = SketchExtensionAndIntersectionProcessing.intersectPair(a, b, true, joint, isTwoEntityClosedLoop);
            isExtend = true;
        }

        // ④ 延长后仍无交点 → 返回空数组：该链无法形成连续轮廓，由调用方终止本链处理并整链丢弃
        if (points.length === 0) {
            return [];
        }

        // 一个交点的情况; 如果是延长后才相交的, 判断交点是否在延长线上, 不是则交点非法, 处理曲线和弧绕一圈回来相交的情况
        if (isExtend === true && points.length === 1 && isAllLine === false &&
            (SketchExtensionAndIntersectionProcessing.isOnSketch(points[0], a) === true  ||
            SketchExtensionAndIntersectionProcessing.isOnSketch(points[0], b) === true)
        ) {
            points = []; // 说明该交点非法, 删除; 注意points只会有一个交点
            return [];
        }

        // ⑤ 截取替换（交点顶替两实体在原始链中的相连端，截取侧由 joint 透传；
        //    边界线段标志透传给截取方法，决定交点越过非相连端时是否整段消失）
        return SketchExtensionAndIntersectionProcessing.cutPair(
            a, b, points[0]!, joint, aIsBoundary, bIsBoundary,
        );
    }

    /**
     * 判断一条选择链的原始数据是否为折线段。
     *
     * 说明：折线段 = 链内全部实体均为直线段（SketchLineObject）的链。仅按几何类型判定，
     * 不限制链长度（单实体链同样返回 true，是否排除由调用方决定）；该判定为折线段
     * 专属处理语义（如边界线段延长后无交点时的特殊处理）提供场景识别。
     *
     * @param originalChain 偏移前原始草图实体链（与偏移链按索引一一对应）。
     * @returns 链内全部实体均为直线段时返回 true，否则返回 false。
     */
    private static isPolylineOriginalChain(originalChain: SketchEntityChain): boolean {
        return originalChain.every((entity) => entity instanceof SketchLineObject);
    }

    /**
     * 只处理其中一个草图实体是圆弧/椭圆弧的情况, 两个都是弧都不处理, 直接返回false, 两个都不是圆弧/椭圆弧的不处理, 直接返回false
     * 弧: 补全为满弧, 然后判断另外一个草图是否在该满弧的内部, 是, 返回true, 不是, 返回false
     *
     * 保留下方可视化debug的代码不变, 我已经将其注释掉
     */
    private static isContain(sketchObj1: OffsetCandidate, sketchObj2: OffsetCandidate): boolean {
        // 满弧多边形外扩系数（相对半径的比例）：
        // 64 段内接弦多边形在弦中点的最大凹陷为 1 - cos(π/64) ≈ 0.0012（相对半径）。
        // 旧实现用固定 1e-9 米外扩，无法覆盖该凹陷，导致恰落在满圆弧边界上的点
        // （如与圆弧端点重合的相连线段端点）被射线法判为外部。
        // 按半径比例外扩 0.002（0.2%），大于弦凹陷 0.0012，保证原满弧边界整体被判定多边形覆盖。
        const CONTAIN_SAGITTA_FACTOR = 0.002;
        // 满弧与被判定实体的采样段数（与全仓弧线 64 段采样口径一致）
        const SAMPLE_SEGMENTS = 64;

        // 类型分类：恰好一个草图实体是圆弧/椭圆弧才继续；两个都是 / 两个都不是时直接返回 false
        const isArcLike1 = sketchObj1 instanceof SketchArcObject || sketchObj1 instanceof SketchEllipseObject;
        const isArcLike2 = sketchObj2 instanceof SketchArcObject || sketchObj2 instanceof SketchEllipseObject;
        if (isArcLike1 === isArcLike2) {
            return false;
        }

        // 弧类实体为"容器"（补全为满弧），另一个实体为"被判定草图"
        const containerArc = (isArcLike1 ? sketchObj1 : sketchObj2) as SketchArcObject | SketchEllipseObject;
        const containedEntity = isArcLike1 ? sketchObj2 : sketchObj1;

        // 复用 SketchEntityExtension 的延申补全方法，把圆弧/椭圆弧补全为满圆/满椭圆
        // （返回新对象、不修改原始弧；原始弧已满时直接克隆返回，幂等）
        const fullArc = containerArc instanceof SketchArcObject
            ? SketchEntityExtension.sketchExtensionArc(containerArc, { header: false, end: true })
            : SketchEntityExtension.sketchExtensionEllipse(containerArc, { header: false, end: true });

        // 满弧多边形化：以参考面局部 2D 坐标把满圆/满椭圆边界采样为闭合多边形，
        // 半径按比例外扩 CONTAIN_SAGITTA_FACTOR 覆盖 64 段内接弦凹陷
        // （pointInPolygon2 本身无容差参数，外扩阈值在此落地）
        const refPlane = containerArc.referencePlane;
        const polygon: [number, number][] = [];
        if (fullArc instanceof SketchArcObject) {
            // 满圆：64 段圆周采样
            const { centerWorld, radius } = fullArc.getArcDefinition();
            const centerLocal = refPlane.worldToLocal(centerWorld.clone());
            const effectiveRadius = radius * (1 + CONTAIN_SAGITTA_FACTOR);
            for (let index = 0; index < SAMPLE_SEGMENTS; index += 1) {
                const angle = (Math.PI * 2 * index) / SAMPLE_SEGMENTS;
                polygon.push([
                    centerLocal.x + effectiveRadius * Math.cos(angle),
                    centerLocal.y + effectiveRadius * Math.sin(angle),
                ]);
            }
        } else {
            // 满椭圆：64 段参数角采样后绕局部圆心旋转 rotation（与 SketchEntityExtension 端点计算口径一致）
            const { centerWorld, radiusX, radiusY, rotation } = fullArc.getEllipseDefinition();
            const centerLocal = refPlane.worldToLocal(centerWorld.clone());
            const effectiveRadiusX = radiusX * (1 + CONTAIN_SAGITTA_FACTOR);
            const effectiveRadiusY = radiusY * (1 + CONTAIN_SAGITTA_FACTOR);
            for (let index = 0; index < SAMPLE_SEGMENTS; index += 1) {
                const angle = (Math.PI * 2 * index) / SAMPLE_SEGMENTS;
                const localPoint = new Vector2(
                    centerLocal.x + effectiveRadiusX * Math.cos(angle),
                    centerLocal.y + effectiveRadiusY * Math.sin(angle),
                ).rotateAround(centerLocal, rotation);
                polygon.push([localPoint.x, localPoint.y]);
            }
        }

        // 采样"被判定草图"的世界坐标点列（与 SketchEntityPreviewOverlayDashLine.sampleEntityPoints
        // 同一口径：线取两端点，圆/弧/椭圆弧按定义采样，样条/样式样条/抛物线取 curvePoints）
        const samplePoints: Vector3[] = [];
        if (containedEntity instanceof SketchLineObject) {
            samplePoints.push(containedEntity.startPoint.position.clone(), containedEntity.endPoint.position.clone());
        } else if (containedEntity instanceof SketchCircleObject) {
            const centerLocal = refPlane.worldToLocal(containedEntity.vec3dCenter.clone());
            for (let index = 0; index <= SAMPLE_SEGMENTS; index += 1) {
                const angle = (Math.PI * 2 * index) / SAMPLE_SEGMENTS;
                samplePoints.push(refPlane.localToWorld(new Vector2(
                    centerLocal.x + containedEntity.radius * Math.cos(angle),
                    centerLocal.y + containedEntity.radius * Math.sin(angle),
                )));
            }
        } else if (containedEntity instanceof SketchArcObject) {
            const definition = containedEntity.getArcDefinition();
            const centerLocal = refPlane.worldToLocal(definition.centerWorld.clone());
            const curve = new EllipseCurve(
                centerLocal.x, centerLocal.y,
                definition.radius, definition.radius,
                definition.startAngle, definition.endAngle,
                definition.clockwise, 0,
            );
            curve.getPoints(definition.segments ?? SAMPLE_SEGMENTS).forEach((point) => {
                samplePoints.push(refPlane.localToWorld(new Vector2(point.x, point.y)));
            });
        } else if (containedEntity instanceof SketchEllipseObject) {
            const definition = containedEntity.getEllipseDefinition();
            const centerLocal = refPlane.worldToLocal(definition.centerWorld.clone());
            const curve = new EllipseCurve(
                centerLocal.x, centerLocal.y,
                definition.radiusX, definition.radiusY,
                definition.startAngle, definition.endAngle,
                definition.clockwise, definition.rotation,
            );
            curve.getPoints(definition.segments ?? SAMPLE_SEGMENTS).forEach((point) => {
                samplePoints.push(refPlane.localToWorld(new Vector2(point.x, point.y)));
            });
        } else if (containedEntity instanceof SketchSplineObject
            || containedEntity instanceof SketchStyleSplineObject
            || containedEntity instanceof SketchParabolaObject) {
            samplePoints.push(...containedEntity.getCurvePoints());
        }

        // 无可用采样点（槽口/集合等复合实体）无法判定，视为不包含
        if (samplePoints.length === 0) {
            return false;
        }

        // 射线法（pointInPolygon2）逐点判定：全部采样点都在满弧多边形内才返回 true
        return samplePoints.every((point) => {
            const local = refPlane.worldToLocal(point.clone());
            return pointInPolygon2([local.x, local.y], polygon);
        });

    }

    /**
     * 点是否在草图实体上
     */
    private static isOnSketch(point: SketchPointObject, sketch: OffsetCandidate): boolean {
        // 判定容差（米）：求交与几何运算的常规数值噪声量级
        const EPS = 1e-9;
        const p = point.position;

        // 直线：点到"可见线段"的距离（投影参数收敛到 [0,1]，点必须在段上而非延长线上）
        if (sketch instanceof SketchLineObject) {
            const start = sketch.startPoint.position;
            const end = sketch.endPoint.position;
            const segmentDir = end.clone().sub(start);
            const lenSq = segmentDir.lengthSq();
            if (lenSq < 1e-24) {
                // 退化线段（起点≈终点）：点与线段起点重合即视为在实体上
                return p.distanceTo(start) <= EPS;
            }
            const projectionT = Math.max(0, Math.min(1, p.clone().sub(start).dot(segmentDir) / lenSq));
            const closestPoint = start.clone().addScaledVector(segmentDir, projectionT);
            return p.distanceToSquared(closestPoint) <= EPS * EPS;
        }

        // 圆弧：径向距离 ≤ 容差 且 交点极角落在弧扫掠区间内（不含补全圆的延长部分）
        if (sketch instanceof SketchArcObject) {
            const definition = sketch.getArcDefinition();
            if (definition.radius <= 1e-12) {
                return false;
            }
            if (Math.abs(p.distanceTo(definition.centerWorld) - definition.radius) > EPS) {
                return false;
            }
            const centerLocal = sketch.referencePlane.worldToLocal(definition.centerWorld.clone());
            const pointLocal = sketch.referencePlane.worldToLocal(p);
            const angle = Math.atan2(pointLocal.y - centerLocal.y, pointLocal.x - centerLocal.x);
            // 角度扫掠区间判定（与 isAngleInArcRange 同一口径：满圆跨度 2π 时任意角度在弧上）
            const TWO_PI = Math.PI * 2;
            const norm = (a: number): number => ((a % TWO_PI) + TWO_PI) % TWO_PI;
            const arcSign = definition.clockwise ? -1 : 1;
            const sweep = norm(arcSign * (definition.endAngle - definition.startAngle));
            return norm(arcSign * (angle - definition.startAngle)) <= sweep + 1e-9;
        }

        // 椭圆弧：参数角落在弧扫掠区间内 且 点到椭圆的距离 ≤ 容差
        if (sketch instanceof SketchEllipseObject) {
            const definition = sketch.getEllipseDefinition();
            if (definition.radiusX <= 1e-12 || definition.radiusY <= 1e-12) {
                return false;
            }
            const cosRotation = Math.cos(definition.rotation);
            const sinRotation = Math.sin(definition.rotation);
            const centerLocal = sketch.referencePlane.worldToLocal(definition.centerWorld.clone());
            const pointLocal = sketch.referencePlane.worldToLocal(p);
            const dx = pointLocal.x - centerLocal.x;
            const dy = pointLocal.y - centerLocal.y;
            // 旋转到椭圆主轴系并反解参数角
            const xPrime = dx * cosRotation + dy * sinRotation;
            const yPrime = -dx * sinRotation + dy * cosRotation;
            const theta = Math.atan2(yPrime / definition.radiusY, xPrime / definition.radiusX);
            const TWO_PI = Math.PI * 2;
            const norm = (a: number): number => ((a % TWO_PI) + TWO_PI) % TWO_PI;
            const arcSign = definition.clockwise ? -1 : 1;
            const sweep = norm(arcSign * (definition.endAngle - definition.startAngle));
            if (norm(arcSign * (theta - definition.startAngle)) > sweep + 1e-9) {
                return false;
            }
            // 点到椭圆距离 ≈ |隐式方程残差| / |梯度模长|
            const nx = xPrime / definition.radiusX;
            const ny = yPrime / definition.radiusY;
            const residual = nx * nx + ny * ny - 1;
            const gradientLength = 2 * Math.hypot(
                xPrime / (definition.radiusX * definition.radiusX),
                yPrime / (definition.radiusY * definition.radiusY),
            );
            if (gradientLength < 1e-12) {
                return false; // 点在中心且梯度退化：距离极大，不在椭圆上
            }
            return Math.abs(residual) / gradientLength <= EPS;
        }

        // 抛物线：侧向参数落在可见参数区间内 且 点到抛物线的距离 ≤ 容差
        if (sketch instanceof SketchParabolaObject) {
            const definition = sketch.getParabolaDefinition();
            const referencePlane = sketch.referencePlane;
            const normal = referencePlane.normal.clone().normalize();
            const axisVector = definition.focalWorld.clone().sub(definition.apexWorld);
            axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
            const focalLength = axisVector.length();
            if (focalLength < 1e-12) {
                return false; // 焦点与顶点重合：抛物线定义无效
            }
            const axisDirWorld = axisVector.normalize();
            const sideDirWorld = new Vector3().crossVectors(axisDirWorld, normal).normalize();
            const apexLocal = referencePlane.worldToLocal(definition.apexWorld.clone());
            const pointLocal = referencePlane.worldToLocal(p);
            const axis2D = referencePlane.worldToLocal(definition.apexWorld.clone().add(axisDirWorld)).sub(apexLocal);
            const side2D = referencePlane.worldToLocal(definition.apexWorld.clone().add(sideDirWorld)).sub(apexLocal);
            const dx = pointLocal.x - apexLocal.x;
            const dy = pointLocal.y - apexLocal.y;
            // 侧向参数 t = (P - apex)·side（side 为单位向量）
            const t = dx * side2D.x + dy * side2D.y;
            // 参数可见区间（兼容端点反向越过）
            const minParam = Math.min(definition.startParameter, definition.endParameter);
            const maxParam = Math.max(definition.startParameter, definition.endParameter);
            if (t < minParam - 1e-10 || t > maxParam + 1e-10) {
                return false;
            }
            // 轴向偏差：点沿轴向与曲线 P(t) 的差 = (P - apex)·axis - t²/(4f)
            const axialOffset = dx * axis2D.x + dy * axis2D.y - (t * t) / (4 * focalLength);
            // 曲线切线沿轴向相对侧向的斜率 = t/(2f)，距离 ≈ |轴向偏差| / √(1 + 斜率²)
            const distance = Math.abs(axialOffset) / Math.hypot(1, t / (2 * focalLength));
            return distance <= EPS;
        }

        // 样条：点到可见曲线（密集采样折线）的最小距离 ≤ 容差
        if (sketch instanceof SketchSplineObject) {
            const curvePoints = sketch.getCurvePoints();
            let minDistanceSq = Number.POSITIVE_INFINITY;
            for (let i = 0; i < curvePoints.length - 1; i++) {
                const segmentStart = curvePoints[i]!;
                const segmentEnd = curvePoints[i + 1]!;
                const segmentDir = segmentEnd.clone().sub(segmentStart);
                const segmentLenSq = segmentDir.lengthSq();
                // 段内投影参数（退化段按段起点参与）
                const projectionT = segmentLenSq > 1e-24
                    ? Math.max(0, Math.min(1, p.clone().sub(segmentStart).dot(segmentDir) / segmentLenSq))
                    : 0;
                const closestPoint = segmentStart.clone().addScaledVector(segmentDir, projectionT);
                const distanceSq = p.distanceToSquared(closestPoint);
                if (distanceSq < minDistanceSq) {
                    minDistanceSq = distanceSq;
                }
            }
            return minDistanceSq <= EPS * EPS;
        }

        // 圆/样式样条/槽口/集合等不支持类型：无口径，直接返回 false
        return false;
    }


    //// 统一入口
    /**
     * 对多条选择链的偏移实体执行延伸和相交处理，并去除冗余自交线段。
     * 
     * @param chains 偏移后的草图实体链。
     * @param originalChains 原始草图实体链（与 chains 按索引一一对应）。
     * @param closedFlags 每条链的闭合标志（与 chains 按索引一一对应），
     *   闭合链需额外处理首尾 `(n-1, 0)` 的连接以形成闭环。
     * @param keptOriginalChains 可选出参：与返回值按索引一一对应的"被保留链"原始实体链。
     *   处理中整链丢弃（链失效）的原始链不会写入本数组，调用方可借此把最终实体
     *   映射回其源实体（如等距圆弧/椭圆弧落地时创建圆心重合关系）。
     * @returns 处理后的草图实体链（已去除冗余线段）。
     */
    static sketchExtensionAndIntersectionProcessing(
        chains: SketchEntityChain[],
        originalChains: SketchEntityChain[],
        closedFlags: boolean[],
        keptOriginalChains?: SketchEntityChain[],
    ): SketchEntityChain[] {
        // 出参数组先清空，避免调用方复用数组时残留上一轮映射
        keptOriginalChains?.splice(0, keptOriginalChains.length);

        // ═══════════════════════════════════════════════════════════
        // 1. 遍历每条选择链
        // ═══════════════════════════════════════════════════════════
        const result: SketchEntityChain[] = [];

        for (let ci = 0; ci < chains.length; ci++) {
            const chain = chains[ci]!;
            const isClosed = closedFlags[ci] ?? false;
            // 偏移前原始实体链（与 chain 按索引一一对应），用于解析各实体的"相连端"；
            // 调用方未提供原始链时回退到偏移后链本身（退化旧行为）
            const originalChain = originalChains[ci] ?? chain;

            // 当前选择链的原始数据是否为折线段（供折线段专属处理分支识别场景；
            // 该标志为后续恢复折线段边界语义的入口，当前版本暂未消费）
            const isAllLine = SketchExtensionAndIntersectionProcessing.isPolylineOriginalChain(originalChain);

            // 是否为"两个草图实体组成的自封闭图形"：闭合且仅两个实体。
            // 该场景偏移后两曲线可能在"另一接缝"交叉，extend=false 的单个候选交点
            // 可能是另一接缝的伪交点，选取层需做接缝侧过滤；其它场景单候选不筛
            const isTwoEntityClosedLoop = isClosed && chain.length === 2;

            // 单实体链无需处理，直接保留
            if (chain.length < 2) {
                result.push([...chain]);
                keptOriginalChains?.push(originalChain);
                continue;
            }

            // 滚动两两处理：[s1, s2, s3, ...] → 先 (s1, s2) 得 [newS1, newS2, s3, ...]，
            // 再 (newS2, s3) 处理，以此类推，处理结果原位替换；
            // 实体被完全吃掉（截取返回 null）时原位置 null，后续对跳过该实体
            const processedChain: (OffsetCandidate | null)[] = [...chain];
            // 链失效标志：某对实体延长后仍无交点时置位，终止本链后续处理并整链丢弃（不渲染）
            let chainInvalid = false;
            for (let i = 0; i < processedChain.length - 1; i++) {
                // 前一轮处理中本实体（或相邻实体）已被完全吃掉（null）：该对无法衔接，
                // 跳过，其余对照常处理（轮廓在该断点处断开）
                const current = processedChain[i];
                const next = processedChain[i + 1];
                const originCurrent = originalChain[i];
                const originNext = originalChain[i+1];
                if (!current || !next) {
                    continue;
                }

                // 如果原草图实体就有包含关系, 则正常运行
                // 在原草图实体没有包含关系的前提下, 如果偏移后草图实体一个包含另外一个 则也不需要处理, 直接返回空
                if (!SketchExtensionAndIntersectionProcessing.isContain(originCurrent, originNext)) {
                    if (SketchExtensionAndIntersectionProcessing.isContain(current, next)) {
                        chainInvalid = true;
                        break;
                    }
                }


                // 边界线段判定（按原始链位置，拓扑不随替换变化）：开放链首实体（i=0）
                // 与尾实体（i+1=n-1）为边界；闭合链无边界（全部按中间线段处理）
                const aIsBoundary = !isClosed && i === 0;
                const bIsBoundary = !isClosed && i + 1 === processedChain.length - 1;
                // 接缝侧信息始终取原始链（偏移前拓扑）：processedChain[i] 可能已被上一轮
                // 处理替换过端点，但其"哪一端与相邻实体相连"由原始实体决定
                const pair = SketchExtensionAndIntersectionProcessing.processPair(
                    current,
                    next,
                    originalChain[i] ?? chain[i]!,
                    originalChain[i + 1] ?? chain[i + 1]!,
                    aIsBoundary,
                    bIsBoundary,
                    isTwoEntityClosedLoop,
                    isAllLine
                );
                // 该对延长后仍无交点：本链无法形成连续轮廓，终止本链后续处理
                if (pair.length === 0) {
                    chainInvalid = true;
                    break;
                }
                const [newA, newB] = pair;
                processedChain[i] = newA;
                processedChain[i + 1] = newB;
            }

            // 闭合链：额外处理首尾 (n-1, 0) 的连接以形成闭环（链已失效或首尾任一方
            // 已被完全吃掉则跳过——轮廓已断开，无法闭合）
            if (isClosed && !chainInvalid) {
                const lastIndex = processedChain.length - 1;
                const last = processedChain[lastIndex];
                const first = processedChain[0];
                if (last && first) {
                    // 两实体闭合链（仅两个实体）：闭合轮与主循环处理的是同一对实体，
                    // 把主循环解析的接缝（交换 aSide/bSide 归一化为本次 (last, first)
                    // 参数顺序）传入排除——同侧配对时平局解析两轮都会取 start-start，
                    // 必须取反才能拿到另一端接缝（详见 resolveJointEnds 注释）
                    let excludeJoint: { aSide: 'start' | 'end'; bSide: 'start' | 'end'; minDistSq: number } | null = null;
                    if (chain.length === 2) {
                        const firstJoint = SketchExtensionAndIntersectionProcessing.resolveJointEnds(
                            originalChain[0],
                            originalChain[1],
                        );
                        if (firstJoint) {
                            excludeJoint = {
                                aSide: firstJoint.bSide,
                                bSide: firstJoint.aSide,
                                minDistSq: firstJoint.minDistSq,
                            };
                        }
                    }
                    const pair = SketchExtensionAndIntersectionProcessing.processPair(
                        last,
                        first,
                        originalChain[lastIndex] ?? chain[lastIndex]!,
                        originalChain[0] ?? chain[0]!,
                        false,
                        false, // 闭合链无边界线段
                        isTwoEntityClosedLoop,
                        isAllLine,
                        excludeJoint,
                    );
                    if (pair.length === 0) {
                        chainInvalid = true;
                    } else {
                        const [newLast, newFirst] = pair;
                        processedChain[lastIndex] = newLast;
                        processedChain[0] = newFirst;
                    }
                }
            }

            // 链失效：整链不渲染（不推入 result），其它链照常处理
            if (chainInvalid) {
                continue;
            }

            // 过滤被完全吃掉的实体（null）；全部消失时整链不推入（不渲染）
            // 只有开口的折线段才可以有null, 其他的有null就整个丢弃(包括含有其他草图的开口组合图形和封闭图形)
            let finalChain = [];
            if (isAllLine && !isClosed) {
                finalChain = processedChain.filter(
                    (entity): entity is OffsetCandidate => entity !== null,
                );
            } else {
                if (processedChain.some((entity) => entity === null)) {
                    finalChain = [];
                } else {
                    finalChain = processedChain;
                }
            }

            if (finalChain.length === 0) {
                continue;
            }

            result.push(finalChain);
            keptOriginalChains?.push(originalChain);
        }

        // 2. 返回处理过后的草图实体
        return result;
    }
}
